/**
 * Drives the OAuth consent popup for connecting an external MCP server.
 *
 * The flow crosses two browser contexts and one origin boundary:
 *
 *   1. This window asks the agents service for an authorization URL.
 *   2. It opens that URL in a popup. The user consents at the *third party's*
 *      authorization server — an origin we cannot script.
 *   3. The AS redirects the popup back to our own callback route, which is a
 *      real page in this SPA and therefore same-origin again.
 *   4. That page reports the query parameters back here and closes itself.
 *
 * Step 4 cannot rely on `window.opener`. Authorization servers that send
 * `Cross-Origin-Opener-Policy: same-origin` (Hugging Face does, on every page)
 * move the popup into a new browsing context group the moment it lands there.
 * That severs the opener link permanently — it stays null after the popup comes
 * home — and from this side `popup.closed` starts reading `true` while the user
 * is still on the consent screen. So the result travels over a same-origin
 * `BroadcastChannel`, which does not care how the windows are related, with
 * `postMessage` kept as a second path for when the link survives.
 *
 * For the same reason `popup.closed` is not treated as a cancellation: it is
 * indistinguishable from a severed link. The caller cancels through an
 * `AbortSignal` (the settings page shows a Cancel button), or the wait times out.
 *
 * A channel reaches every tab on this origin, so messages are matched on the
 * `state` the authorize URL was issued with — another tab's connect, or a stale
 * callback, is ignored rather than redeemed here.
 *
 * The popup is opened *before* the authorize URL is fetched. Browsers only
 * allow `window.open` synchronously inside a user gesture; opening it after an
 * `await` gets it blocked as a pop-up.
 */

const POPUP_FEATURES = 'width=680,height=820,menubar=no,toolbar=no,location=yes'

/** Message type for the authorization result; must match the callback view. */
export const MCP_OAUTH_MESSAGE = 'paice:mcp-oauth-callback'

/** Sent back by the waiting window so the callback page knows it can close. */
export const MCP_OAUTH_ACK = 'paice:mcp-oauth-ack'

/** BroadcastChannel shared by this module and the callback view. */
export const MCP_OAUTH_CHANNEL = 'paice-mcp-oauth'

export class McpOAuthCancelled extends Error {
    constructor(message = 'The connection was cancelled.') {
        super(message)
        this.name = 'McpOAuthCancelled'
    }
}

/** Open a channel, or null where BroadcastChannel is unavailable. */
export function openOAuthChannel() {
    try {
        return typeof BroadcastChannel === 'function' ? new BroadcastChannel(MCP_OAUTH_CHANNEL) : null
    } catch (e) {
        return null
    }
}

/**
 * Open a popup synchronously, so it survives the browser's pop-up blocker.
 * @returns {Window|null} null when the browser blocked it outright.
 */
export function openOAuthPopup() {
    return window.open('', 'paice-mcp-oauth', POPUP_FEATURES)
}

/** The `state` parameter the authorize URL carries, if any. */
function stateOf(authorizationUrl) {
    try {
        return new URL(authorizationUrl).searchParams.get('state')
    } catch (e) {
        return null
    }
}

/**
 * Wait for the callback page to report the authorization response.
 *
 * @param {Window} popup - the window returned by {@link openOAuthPopup}
 * @param {Object} [options]
 * @param {?string} [options.expectedState] - only accept a result carrying this state
 * @param {AbortSignal} [options.signal] - aborting rejects with {@link McpOAuthCancelled}
 * @param {number} [options.timeoutMs=300000] - give up after this long. Sized
 *   for a human getting through a consent screen, and shorter than the pending
 *   credential row's server-side TTL so the failure surfaces here first.
 * @returns {Promise<{code: string, state: string, iss: ?string}>}
 */
export function awaitOAuthCallback(popup, { expectedState = null, signal, timeoutMs = 300000 } = {}) {
    return new Promise((resolve, reject) => {
        let settled = false
        const channel = openOAuthChannel()

        const closePopup = () => {
            try { if (popup && !popup.closed) popup.close() } catch (e) { /* cross-origin */ }
        }

        const cleanup = () => {
            window.removeEventListener('message', onMessage)
            if (channel) {
                channel.removeEventListener('message', onChannelMessage)
                channel.close()
            }
            if (signal) signal.removeEventListener('abort', onAbort)
            clearTimeout(timeoutTimer)
        }

        const finish = (fn, value) => {
            if (settled) return
            settled = true
            cleanup()
            fn(value)
        }

        const accept = (data) => {
            if (!data || data.type !== MCP_OAUTH_MESSAGE) return
            if (expectedState && data.state !== expectedState) return

            // Tell the callback page someone took it, so it can close itself
            // instead of asking the user to.
            if (channel) {
                try { channel.postMessage({ type: MCP_OAUTH_ACK, state: data.state }) } catch (e) { /* closed */ }
            }

            if (data.error) {
                finish(reject, new Error(data.errorDescription || data.error))
                return
            }
            finish(resolve, { code: data.code, state: data.state, iss: data.iss || null })
        }

        const onMessage = (event) => {
            // Only ever trust our own origin. The popup visits a third-party
            // authorization server on the way, and that page can post to this
            // window too — without this check it could hand us an attacker's
            // authorization code to redeem.
            if (event.origin !== window.location.origin) return
            accept(event.data)
        }

        // BroadcastChannel is same-origin by construction.
        const onChannelMessage = (event) => accept(event.data)

        const onAbort = () => {
            closePopup()
            finish(reject, new McpOAuthCancelled())
        }

        window.addEventListener('message', onMessage)
        if (channel) channel.addEventListener('message', onChannelMessage)
        if (signal) {
            if (signal.aborted) { onAbort(); return }
            signal.addEventListener('abort', onAbort)
        }

        const timeoutTimer = setTimeout(() => {
            closePopup()
            finish(reject, new McpOAuthCancelled('The connection timed out. Try again.'))
        }, timeoutMs)
    })
}

/**
 * Run the whole connect: open popup, fetch URL, navigate, await the callback.
 *
 * @param {Object} args
 * @param {Function} args.start - resolves to `{ authorizationUrl, ... }`
 * @param {AbortSignal} [args.signal] - cancels the wait
 * @returns {Promise<{code: string, state: string, iss: ?string}>}
 */
export async function runOAuthConnect({ start, signal }) {
    const popup = openOAuthPopup()
    if (!popup) {
        throw new Error(
            'Your browser blocked the connection window. Allow pop-ups for this site and try again.',
        )
    }

    let authorizationUrl
    try {
        const result = await start()
        authorizationUrl = result?.authorizationUrl
        if (!authorizationUrl) throw new Error('The service did not return an authorization URL.')
    } catch (e) {
        try { popup.close() } catch (err) { /* already gone */ }
        throw e
    }

    popup.location.href = authorizationUrl
    return awaitOAuthCallback(popup, { expectedState: stateOf(authorizationUrl), signal })
}
